/**
 * FormRow
 *
 * Thin wrapper around Mantine's `<Group>` for laying out paired form
 * inputs side by side (e.g. First Name / Last Name in Add Contact).
 *
 * Why this exists:
 *
 * Mantine's `<Group>` defaults to `align="center"`, which vertically
 * centres its children. When one input in a row renders a validation
 * error message under it, that input grows taller and the row recentres,
 * which makes the *labels* of the two inputs no longer line up
 * horizontally — a small but noticeable visual bug. The fix is
 * `align="flex-start"` so labels stay pinned to the top of each cell
 * regardless of error-message height.
 *
 * Centralising the row defaults in this primitive means contributors
 * reach for `<FormRow>` instead of having to remember `align="flex-start"`
 * every time they pair inputs, and a future change to the row defaults
 * (gap, wrap behaviour, etc.) lands in one place rather than every form.
 *
 * Defaults vs `<Group>`:
 *
 * - `align="flex-start"` — see above; keeps labels aligned across error states.
 * - `grow={true}` — paired inputs almost always want to share the available
 *   width equally, matching the existing `<Group grow>` calls we're replacing.
 * - `wrap="wrap"` — on iPhone SE (~375px viewport) two side-by-side inputs are
 *   tight even before an error message lands; wrapping lets desktop render
 *   them side-by-side and mobile stack them. Callers can opt out with
 *   `wrap="nowrap"` for the rare row that must stay horizontal at every width.
 * - `gap="md"` — matches the existing implicit Mantine default; explicit so
 *   it's documented and tweakable without surprise.
 *
 * Usage:
 *
 * ```tsx
 * <FormRow>
 *   <TextInput label="First Name" {...form.getInputProps('firstName')} />
 *   <TextInput label="Last Name" {...form.getInputProps('lastName')} />
 * </FormRow>
 * ```
 *
 * Override any default by passing the corresponding `<Group>` prop:
 *
 * ```tsx
 * <FormRow wrap="nowrap" gap="xs">
 *   ...
 * </FormRow>
 * ```
 *
 * See also: `.cursor/rules/form-validation.mdc` for the broader
 * forms-in-modals convention this primitive participates in.
 */

import { Group, type GroupProps } from '@mantine/core';

/**
 * Props for {@link FormRow}. Identical to Mantine's `<Group>` props so
 * callers can override any default by simply passing the corresponding
 * prop (e.g. `wrap="nowrap"`, `justify="space-between"`).
 */
export type FormRowProps = GroupProps;

/**
 * Layout primitive for paired form inputs. See file docstring for why
 * this exists and how its defaults differ from raw `<Group>`.
 */
export const FormRow = ({
  align = 'flex-start',
  grow = true,
  wrap = 'wrap',
  gap = 'md',
  ...rest
}: FormRowProps) => (
  <Group align={align} grow={grow} wrap={wrap} gap={gap} {...rest} />
);
